
import { useEffect } from 'react';

export default function HomePage() {
  useEffect(() => {
    document.title = 'Supernova Mobile Detailing - Luxury Mobile Car Detailing Services Toronto GTA';
    
    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateMetaProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic SEO meta tags
    updateMetaTag('description', 'Professional mobile auto detailing service in Greater Toronto Area. Luxury car care at your doorstep with satisfaction guarantee. Premium car wash, interior cleaning, paint protection. Book your appointment today!');
    updateMetaTag('keywords', 'mobile car detailing,auto detailing Toronto,car wash GTA,luxury car care,professional detailing,mobile car wash,car cleaning Toronto');
    updateMetaTag('author', 'Supernova Mobile Detailing');
    updateMetaTag('robots', 'index, follow');
    
    // Geo tags for local SEO
    updateMetaTag('geo.region', 'CA-ON');
    updateMetaTag('geo.placename', 'Toronto, Ontario, Canada');
    updateMetaTag('geo.position', '43.6532;-79.3832');
    updateMetaTag('ICBM', '43.6532, -79.3832');
    
    // Open Graph tags
    updateMetaProperty('og:title', 'Supernova Mobile Detailing - Luxury Mobile Car Detailing Services Toronto GTA');
    updateMetaProperty('og:description', 'Professional mobile auto detailing service in Greater Toronto Area. Luxury car care at your doorstep with satisfaction guarantee. Book your appointment today!');
    updateMetaProperty('og:type', 'website');
    updateMetaProperty('og:url', window.location.href);
    updateMetaProperty('og:site_name', 'Supernova Mobile Detailing');
    updateMetaProperty('og:locale', 'en_CA');
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', 'Supernova Mobile Detailing - Luxury Mobile Car Detailing Services Toronto GTA');
    updateMetaTag('twitter:description', 'Professional mobile auto detailing service in Greater Toronto Area. Luxury car care at your doorstep with satisfaction guarantee.');
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.href;

    // Last modified
    updateMetaTag('last-modified', new Date().toISOString());

    // Add Schema.org JSON-LD
    const addJsonLd = (data: any) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    // Remove existing JSON-LD scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Local Business Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#business`,
      "name": "Supernova Mobile Detailing",
      "description": "Professional mobile auto detailing service in Greater Toronto Area. Luxury car care at your doorstep with satisfaction guarantee.",
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "telephone": "+1-647-584-0133",
      "email": "info@supernovamobiledetailing.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Toronto",
        "addressRegion": "ON",
        "addressCountry": "CA",
        "areaServed": "Greater Toronto Area"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 43.6532,
        "longitude": -79.3832
      },
      "openingHours": [
        "Mo-Sa 08:00-18:00",
        "Su 10:00-16:00"
      ],
      "priceRange": "$149-$299",
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 43.6532,
          "longitude": -79.3832
        },
        "geoRadius": "50000"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Auto Detailing Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Regular Wash",
              "description": "Exterior wash, rim and tire cleaning, and light interior wipe-down"
            },
            "price": "149.99",
            "priceCurrency": "CAD"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Interior Only",
              "description": "Comprehensive interior detailing for all vehicle types"
            },
            "price": "189.99",
            "priceCurrency": "CAD"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Showroom Package",
              "description": "Complete inside and outside detailing service"
            },
            "price": "239.99",
            "priceCurrency": "CAD"
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "500",
        "bestRating": "5",
        "worstRating": "1"
      },
      "sameAs": [
        "https://www.facebook.com/supernovamobiledetailing",
        "https://www.instagram.com/supernovamobiledetailing"
      ]
    });

    // Website Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "name": "Supernova Mobile Detailing",
      "description": "Professional mobile auto detailing service in Greater Toronto Area",
      "publisher": {
        "@type": "Organization",
        "name": "Supernova Mobile Detailing",
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#business`
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    });

    // WebPage Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#webpage`,
      "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
      "name": "Supernova Mobile Detailing - Luxury Mobile Car Detailing Services Toronto GTA",
      "description": "Professional mobile auto detailing service in Greater Toronto Area. Luxury car care at your doorstep with satisfaction guarantee.",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`
      },
      "about": {
        "@type": "LocalBusiness",
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#business`
      },
      "mainEntity": {
        "@type": "LocalBusiness",
        "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#business`
      }
    });

    // FAQ Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What areas do you serve?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We provide mobile auto detailing services throughout the Greater Toronto Area, including Toronto, Mississauga, Markham, Vaughan, Brampton, and Richmond Hill."
          }
        },
        {
          "@type": "Question",
          "name": "How long does a detailing service take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Service time varies by package: Regular Wash takes 1-2 hours, Interior Only takes 2-3 hours, and our Showroom Package takes 3-4 hours depending on vehicle size and condition."
          }
        },
        {
          "@type": "Question",
          "name": "Do you provide all equipment and water?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we bring all professional-grade equipment, premium products, and our own water supply. You don't need to provide anything - just a parking space for your vehicle."
          }
        },
        {
          "@type": "Question",
          "name": "What payment methods do you accept?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We accept cash, credit cards, debit cards, and e-transfer for your convenience. Payment is due upon completion of service."
          }
        }
      ]
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-red-400/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-red-400 to-red-600 rounded-full flex items-center justify-center">
                <i className="ri-car-washing-line text-black text-xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-white">
                <span>Supernova</span>
                <span className="text-red-400 ml-2">Mobile Detailing</span>
              </h1>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button className="text-white hover:text-red-400 transition-colors cursor-pointer">Home</button>
              <button className="text-white hover:text-red-400 transition-colors cursor-pointer">About</button>
              <button className="text-white hover:text-red-400 transition-colors cursor-pointer">Services</button>
              <button className="text-white hover:text-red-400 transition-colors cursor-pointer">Gallery</button>
              <button className="text-white hover:text-red-400 transition-colors cursor-pointer">Reviews</button>
              <button className="text-white hover:text-red-400 transition-colors cursor-pointer">Contact</button>
              <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-400 to-red-600 text-white hover:from-red-500 hover:to-red-700 shadow-lg hover:shadow-xl px-4 py-2 text-sm rounded-md">
                Book Now
              </a>
            </nav>
            <button className="md:hidden text-white text-2xl cursor-pointer">
              <i className="ri-menu-line"></i>
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                <span className="text-red-400">Luxury</span> Auto Detailing
                <br />
                <span className="text-3xl md:text-5xl font-light">at Your Doorstep</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Professional mobile detailing service bringing showroom-level cleaning and protection directly to your location
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-400 to-red-600 text-white hover:from-red-500 hover:to-red-700 shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl transform hover:scale-105 bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600">
                  <i className="ri-phone-line mr-2"></i>Call Now: (647) 584-0133
                </button>
                <button className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-white px-8 py-4 text-lg rounded-xl transform hover:scale-105">
                  <i className="ri-eye-line mr-2"></i>View Services
                </button>
              </div>
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-map-pin-line text-red-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">100% Mobile</h3>
                  <p className="text-gray-400 text-sm">We come to your location</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-red-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Guaranteed</h3>
                  <p className="text-gray-400 text-sm">Satisfaction guaranteed</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-award-line text-red-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Professional</h3>
                  <p className="text-gray-400 text-sm">Premium products & equipment</p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-red-400 text-2xl"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                  About <span className="text-red-600">Supernova Mobile Detailing</span>
                </h2>
                <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              </div>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    alt="Professional auto detailing service" 
                    title="Professional auto detailing service mobile car wash Toronto"
                    className="rounded-2xl shadow-2xl object-cover w-full h-96" 
                    src="https://public.readdy.ai/ai/img_res/73a43a2c077681cd56b06a64e7fe89ae.jpg"
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    At Supernova Mobile Detailing, we're committed to delivering exceptional automotive care that exceeds your expectations. Our team of skilled professionals brings years of experience and passion for perfection to every vehicle we service.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    We understand that your vehicle is more than just transportation—it's an investment that deserves the highest level of care. That's why we use only premium products and state-of-the-art equipment to ensure your car receives the royal treatment it deserves.
                  </p>
                  <div className="grid grid-cols-2 gap-6 mt-8">
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-red-600 mb-2">500+</div>
                      <div className="text-gray-600">Cars Detailed</div>
                    </div>
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                      <div className="text-gray-600">Satisfaction Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Our <span className="text-red-400">Services</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Choose from our range of professional detailing services, all performed at your location
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-red-400/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-400/20">
                    <i className="ri-car-washing-line text-2xl text-red-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Regular Wash</h3>
                  <div className="text-3xl font-bold text-red-400 mb-4">Starting at $149.99</div>
                  <p className="text-gray-400 mb-6">Exterior wash, rim and tire cleaning, and light interior wipe-down</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Exterior wash & dry
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Rim & tire cleaning
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Light interior wipe-down
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Window cleaning
                  </li>
                </ul>
                <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-white px-6 py-3 text-base rounded-lg w-full">
                  Book Now
                </a>
              </div>

              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-red-400/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-400/20">
                    <i className="ri-brush-line text-2xl text-red-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Interior Only</h3>
                  <div className="text-3xl font-bold text-red-400 mb-4">Starting at $189.99</div>
                  <p className="text-gray-400 mb-6">Comprehensive interior detailing for all vehicle types</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Sedan - $189.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>SUV 5-seater - $214.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>7-seater & Truck - $229.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Mini Van - $249.99
                  </li>
                </ul>
                <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-white px-6 py-3 text-base rounded-lg w-full">
                  Book Now
                </a>
              </div>

              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-red-400 shadow-2xl shadow-red-400/20">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-red-400 text-white px-4 py-2 rounded-full text-sm font-bold">Most Popular</span>
                </div>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-400">
                    <i className="ri-star-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Showroom Package</h3>
                  <div className="text-3xl font-bold text-red-400 mb-4">Starting at $239.99</div>
                  <p className="text-gray-400 mb-6">Complete inside and outside detailing service</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Sedan - $239.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>SUV 5-seater - $264.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>7-seater & Truck - $279.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Mini Van - $299.99
                  </li>
                </ul>
                <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-400 to-red-600 text-white hover:from-red-500 hover:to-red-700 shadow-lg hover:shadow-xl px-6 py-3 text-base rounded-lg w-full">
                  Book Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Before & <span className="text-red-600">After Gallery</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                See the incredible transformations we achieve with our professional detailing services
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Interior Deep Clean</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - Before" 
                        title="Car interior before professional detailing service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/dc7f0d1e3d4ef1a85759633a824897fe.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - After" 
                        title="Car interior after professional detailing service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/65dace0b12a7c4cda0225b9ec5efabf4.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Exterior Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - Before" 
                        title="Car exterior before professional detailing service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - After" 
                        title="Car exterior after professional detailing service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">SUV Complete Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - Before" 
                        title="SUV before complete detailing service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8609aae2015379ebb1d01a8c1fa67a9a.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - After" 
                        title="SUV after complete detailing service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/854be7e3dba04760f5d9ed0529753ceb.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Paint Restoration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - Before" 
                        title="Car paint before restoration service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://public.readdy.ai/ai/img_res/e27b3530b67aecabfe947b498885eadc.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - After" 
                        title="Car paint after restoration service"
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105" 
                        src="https://public.readdy.ai/ai/img_res/e4806feb1b6921c3255c9d38baff7521.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                What Our <span className="text-red-400">Customers Say</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Don't just take our word for it - hear from our satisfied customers across the GTA
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">M</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Michael Chen</h4>
                    <p className="text-gray-400 text-sm">Toronto, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Absolutely incredible service! My BMW looks better than the day I bought it. The team was professional, punctual, and the attention to detail was outstanding. Will definitely be using Supernova Mobile Detailing again!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>BMW 3 Series
                  </p>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">S</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Sarah Johnson</h4>
                    <p className="text-gray-400 text-sm">Mississauga, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "I was amazed by the transformation of my SUV's interior. The kids had made quite a mess, but Supernova made it look brand new. The convenience of mobile service is unbeatable!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Honda Pilot
                  </p>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">D</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">David Rodriguez</h4>
                    <p className="text-gray-400 text-sm">Markham, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Professional, reliable, and exceptional results. My Mercedes has never looked better. The paint correction work was flawless and the customer service was top-notch. Highly recommended!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Mercedes C-Class
                  </p>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">E</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Emily Thompson</h4>
                    <p className="text-gray-400 text-sm">Vaughan, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "The combo package was worth every penny. Both interior and exterior detailing exceeded my expectations. The team arrived on time and worked with such precision. My car feels like new again!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Audi Q5
                  </p>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">J</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">James Wilson</h4>
                    <p className="text-gray-400 text-sm">Brampton, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Outstanding service from start to finish. The booking process was easy, and the results speak for themselves. My truck's interior was completely transformed. These guys are true professionals!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Ford F-150
                  </p>
                </div>
              </div>

              <div className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">L</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Lisa Park</h4>
                    <p className="text-gray-400 text-sm">Richmond Hill, ON</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "I've tried other detailing services before, but Supernova is in a league of their own. The attention to detail and quality of work is exceptional. My Lexus has never looked this good!"
                </p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Lexus RX
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center mt-12">
              <div className="inline-flex items-center space-x-8 bg-gray-900 rounded-2xl p-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-400 mb-2">4.9/5</div>
                  <div className="text-gray-300">Average Rating</div>
                </div>
                <div className="w-px h-16 bg-gray-700"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-400 mb-2">500+</div>
                  <div className="text-gray-300">Happy Customers</div>
                </div>
                <div className="w-px h-16 bg-gray-700"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-400 mb-2">100%</div>
                  <div className="text-gray-300">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Book Your <span className="text-red-600">Appointment</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-8">
                Ready to give your vehicle the royal treatment? Call us now or schedule online
              </p>
              <div className="flex justify-center mb-12">
                <div className="bg-white rounded-full p-1 shadow-lg">
                  <button className="px-8 py-4 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer bg-green-500 text-white hover:bg-green-600 shadow-md mr-2">
                    <i className="ri-phone-line mr-2"></i>Call Now: (647) 584-0133
                  </button>
                  <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer text-gray-600 hover:text-black hover:bg-gray-100">
                    <i className="ri-calendar-line mr-2"></i>Schedule Online
                  </a>
                </div>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h3 className="text-2xl font-bold text-black mb-6">Get In Touch</h3>
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-phone-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Phone</h4>
                        <button className="text-green-600 hover:text-green-700 font-semibold cursor-pointer">
                          (647) 584-0133
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Email</h4>
                        <p className="text-gray-600">info@supernovamobiledetailing.com</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-map-pin-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Service Area</h4>
                        <p className="text-gray-600">Greater Toronto Area</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-time-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Hours</h4>
                        <p className="text-gray-600">Mon-Sat: 8AM-6PM<br />Sun: 10AM-4PM</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <button className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl transition-colors cursor-pointer shadow-lg">
                      <i className="ri-phone-line mr-3 text-xl"></i>Call Now for Instant Booking
                      <div className="text-sm font-normal mt-1">(647) 584-0133</div>
                    </button>
                  </div>
                </div>
                <div className="bg-black rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Why Choose Supernova?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>100% mobile service - we come to you
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Professional‑grade products & equipment
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Satisfaction guaranteed
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Fully insured & bonded
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Eco‑friendly cleaning solutions
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-black mb-6">Quick Booking Options</h3>
                <div className="space-y-6">
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-phone-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-green-700">Call for Immediate Service</h4>
                        <p className="text-green-600">Fastest way to book your appointment</p>
                      </div>
                    </div>
                    <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-phone-line mr-2"></i>Call (647) 584-0133
                    </a>
                  </div>
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-calendar-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-blue-700">Schedule Online</h4>
                        <p className="text-blue-600">Pick your preferred time slot</p>
                      </div>
                    </div>
                    <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-calendar-line mr-2"></i>View Available Times
                    </a>
                  </div>
                </div>
                <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    <i className="ri-information-line mr-2 text-blue-500"></i>Quick Booking Tips
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Have your vehicle type and preferred service ready</li>
                    <li>• Mention your location in the GTA</li>
                    <li>• Ask about our combo packages for best value</li>
                    <li>• Same-day service available for urgent requests</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-red-400 to-red-600 rounded-full flex items-center justify-center">
                  <i className="ri-car-washing-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold">
                  <span>Supernova</span>
                  <span className="text-red-400 ml-2">Mobile Detailing</span>
                </h3>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Professional mobile auto detailing service bringing luxury car care directly to your doorstep. Serving the Greater Toronto Area with premium products and exceptional service.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-google-line"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-6 text-red-400">Quick Links</h4>
              <ul className="space-y-3">
                <li><button className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Home</button></li>
                <li><button className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">About Us</button></li>
                <li><button className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Services</button></li>
                <li><button className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Gallery</button></li>
                <li><button className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-6 text-red-400">Contact Info</h4>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-400">
                  <i className="ri-phone-line mr-3 text-red-400"></i>(647) 584-0133
                </li>
                <li className="flex items-center text-gray-400">
                  <i className="ri-mail-line mr-3 text-red-400"></i>info@supernovamobiledetailing.com
                </li>
                <li className="flex items-center text-gray-400">
                  <i className="ri-map-pin-line mr-3 text-red-400"></i>Greater Toronto Area
                </li>
                <li className="flex items-center text-gray-400">
                  <i className="ri-time-line mr-3 text-red-400"></i>Mon-Sat: 8AM-6PM
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2025 Supernova Mobile Detailing. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-red-400 text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-red-400 text-sm transition-colors cursor-pointer">Terms of Service</a>
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-400 text-sm transition-colors cursor-pointer">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
